import { useState, useCallback } from "react";
import { UserCog, Plus, KeyRound, UserMinus, Shield, ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import {
  useAdminUsers,
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeactivateAdminUser,
  useResetAdminPassword,
} from "@/hooks/admin/useOperations";
import type { AdminUser, AdminRole, CreateAdminUserInput } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: "Super admin",
  manager: "Manager",
  support: "Support",
  finance: "Finance",
  logistics: "Logistique",
};

const ROLE_COLORS: Record<AdminRole, string> = {
  super_admin: "bg-red-100 text-red-800 border-red-200",
  manager: "bg-blue-100 text-blue-800 border-blue-200",
  support: "bg-amber-100 text-amber-800 border-amber-200",
  finance: "bg-emerald-100 text-emerald-800 border-emerald-200",
  logistics: "bg-purple-100 text-purple-800 border-purple-200",
};

const RESOURCES = [
  { key: "orders", label: "Commandes" },
  { key: "products", label: "Produits" },
  { key: "customers", label: "Clients" },
  { key: "payments", label: "Paiements" },
  { key: "deliveries", label: "Livraisons" },
  { key: "inventory", label: "Stock" },
  { key: "reports", label: "Rapports" },
  { key: "settings", label: "Paramètres" },
];

const ACTIONS = ["read", "write", "delete"];

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "role",
    label: "Rôle",
    type: "select",
    options: [
      { value: "super_admin", label: "Super admin" },
      { value: "manager", label: "Manager" },
      { value: "support", label: "Support" },
      { value: "finance", label: "Finance" },
      { value: "logistics", label: "Logistique" },
    ],
  },
  {
    key: "is_active",
    label: "Statut",
    type: "select",
    options: [
      { value: "true", label: "Actif" },
      { value: "false", label: "Inactif" },
    ],
  },
  { key: "q", label: "Rechercher…", type: "search" },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create user modal
// ---------------------------------------------------------------------------

interface CreateUserModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateAdminUserInput) => Promise<void>;
}

function CreateUserModal({ open, onClose, isSaving, onSave }: CreateUserModalProps) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("support");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setFullName("");
    setEmail("");
    setRole("support");
    setPermissions([]);
    setError(null);
    onClose();
  }, [onClose]);

  const togglePermission = useCallback((perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!fullName.trim()) {
      setError("Le nom complet est requis.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Une adresse e-mail valide est requise.");
      return;
    }
    setError(null);
    try {
      await onSave({ full_name: fullName.trim(), email: email.trim(), role, permissions });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [fullName, email, role, permissions, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvel administrateur"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Nom complet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              placeholder="Amadou Diallo"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Adresse e-mail <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              placeholder="amadou@cieda.sn"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">Rôle</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AdminRole)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-stone-700">Permissions</p>
          <div className="space-y-3 rounded-md border border-stone-200 p-3">
            {RESOURCES.map((resource) => (
              <div key={resource.key}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-stone-500">
                  {resource.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map((action) => {
                    const perm = `${resource.key}:${action}`;
                    const checked = permissions.includes(perm);
                    return (
                      <label
                        key={perm}
                        className="flex cursor-pointer items-center gap-1.5 text-xs text-stone-600"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePermission(perm)}
                          className="rounded border-stone-300"
                        />
                        {action}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Edit user modal
// ---------------------------------------------------------------------------

interface EditUserModalProps {
  readonly user: AdminUser | null;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (id: string, input: { role: AdminRole; permissions: string[] }) => Promise<void>;
}

function EditUserModal({ user, onClose, isSaving, onSave }: EditUserModalProps) {
  const [role, setRole] = useState<AdminRole>(user?.role ?? "support");
  const [permissions, setPermissions] = useState<string[]>(
    user ? [...user.permissions] : [],
  );
  const [error, setError] = useState<string | null>(null);

  // Reset when user changes
  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const togglePermission = useCallback((perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!user) return;
    setError(null);
    try {
      await onSave(user.id, { role, permissions });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [user, role, permissions, onSave, handleClose]);

  if (!user) return null;

  return (
    <AdminModal
      open={!!user}
      onClose={handleClose}
      title={`Modifier — ${user.full_name}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">Rôle</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AdminRole)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-stone-700">Permissions</p>
          <div className="space-y-3 rounded-md border border-stone-200 p-3">
            {RESOURCES.map((resource) => (
              <div key={resource.key}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-stone-500">
                  {resource.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map((action) => {
                    const perm = `${resource.key}:${action}`;
                    const checked = permissions.includes(perm);
                    return (
                      <label
                        key={perm}
                        className="flex cursor-pointer items-center gap-1.5 text-xs text-stone-600"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePermission(perm)}
                          className="rounded border-stone-300"
                        />
                        {action}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AdminUsers() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);

  const params = {
    page,
    limit: 20,
    role: (filters.role as AdminRole) ?? undefined,
    is_active: filters.is_active != null
      ? filters.is_active === "true"
      : undefined,
    q: (filters.q as string) ?? undefined,
  };

  const { data, isLoading } = useAdminUsers(params);
  const createMutation = useCreateAdminUser();
  const updateMutation = useUpdateAdminUser();
  const deactivateMutation = useDeactivateAdminUser();
  const resetPasswordMutation = useResetAdminPassword();

  const users = data?.users ?? [];
  const pagination = data?.pagination;

  // KPI calculations from loaded data
  const totalActive = users.filter((u) => u.is_active).length;
  const totalSuperAdmin = users.filter((u) => u.role === "super_admin").length;
  const lastLogin = users
    .map((u) => u.last_login_at)
    .filter(Boolean)
    .sort()
    .at(-1) ?? null;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateAdminUserInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const handleUpdate = useCallback(
    async (id: string, input: { role: AdminRole; permissions: string[] }) => {
      await updateMutation.mutateAsync({ id, input });
    },
    [updateMutation],
  );

  const handleDeactivate = useCallback(
    async (id: string) => {
      if (!confirm("Désactiver cet administrateur ?")) return;
      await deactivateMutation.mutateAsync(id);
    },
    [deactivateMutation],
  );

  const handleResetPassword = useCallback(
    async (id: string) => {
      if (!confirm("Envoyer un lien de réinitialisation du mot de passe ?")) return;
      await resetPasswordMutation.mutateAsync(id);
    },
    [resetPasswordMutation],
  );

  const handleToggleMfa = useCallback(
    async (user: AdminUser) => {
      const enable = !user.mfa_enabled;
      const message = enable
        ? `Activer la 2FA pour ${user.full_name} ?`
        : `Désactiver la 2FA pour ${user.full_name} ?`;
      if (!confirm(message)) return;
      await updateMutation.mutateAsync({
        id: user.id,
        input: { mfa_enabled: enable },
      });
    },
    [updateMutation],
  );

  const columns: readonly ColumnDef<AdminUser>[] = [
    {
      id: "full_name",
      header: "Nom",
      accessorKey: "full_name",
    },
    {
      id: "email",
      header: "E-mail",
      accessorKey: "email",
    },
    {
      id: "role",
      header: "Rôle",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[row.role]}`}
        >
          {ROLE_LABELS[row.role]}
        </span>
      ),
    },
    {
      id: "is_active",
      header: "Statut",
      cell: (row) =>
        row.is_active ? (
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
            Actif
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-600">
            Inactif
          </span>
        ),
    },
    {
      id: "mfa",
      header: "2FA",
      cell: (row) => (
        <button
          onClick={() => handleToggleMfa(row)}
          disabled={updateMutation.isPending}
          className={`rounded p-1 transition-colors ${
            row.mfa_enabled
              ? "text-emerald-600 hover:bg-emerald-50"
              : "text-stone-400 hover:bg-stone-100"
          }`}
          title={row.mfa_enabled ? "2FA activée — cliquer pour désactiver" : "2FA désactivée — cliquer pour activer"}
        >
          {row.mfa_enabled ? (
            <ShieldCheck className="h-4 w-4" />
          ) : (
            <ShieldOff className="h-4 w-4" />
          )}
        </button>
      ),
    },
    {
      id: "last_login_at",
      header: "Dernière connexion",
      cell: (row) => (
        <span className="text-stone-500">{formatDate(row.last_login_at)}</span>
      ),
    },
    {
      id: "created_at",
      header: "Créé le",
      cell: (row) => (
        <span className="text-stone-500">{formatDate(row.created_at)}</span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => setEditUser(row)}
            className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            title="Modifier"
          >
            <Shield className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => handleResetPassword(row.id)}
            className="rounded p-1 text-stone-400 hover:bg-amber-50 hover:text-amber-700"
            title="Réinitialiser mot de passe"
          >
            <KeyRound className="h-3.5 w-3.5" />
          </button>
          {row.is_active && (
            <button
              onClick={() => handleDeactivate(row.id)}
              className="rounded p-1 text-stone-400 hover:bg-red-50 hover:text-red-700"
              title="Désactiver"
            >
              <UserMinus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <UserCog className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Utilisateurs admin
              </h1>
              <p className="text-sm text-stone-500">
                Gestion des accès et permissions
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouvel admin
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total admins"
            value={pagination?.total ?? users.length}
            icon={<UserCog className="h-4 w-4" />}
          />
          <KPICard
            label="Actifs"
            value={totalActive}
          />
          <KPICard
            label="Super admins"
            value={totalSuperAdmin}
          />
          <KPICard
            label="Dernière connexion"
            value={lastLogin ? formatDate(lastLogin) : "—"}
          />
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar
            filters={FILTER_CONFIGS}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={users}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucun administrateur trouvé."
        />
      </div>

      {/* Create modal */}
      <CreateUserModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />

      {/* Edit modal */}
      <EditUserModal
        user={editUser}
        onClose={() => setEditUser(null)}
        isSaving={updateMutation.isPending}
        onSave={handleUpdate}
      />
    </AdminLayout>
  );
}
